<template>
  <div class="campaign-data-page">
    <a-row :gutter="[24, 24]">
      <a-col :span="24">
        <a-card :bordered="false" class="filter-card" title="Campaign Data">
          <a-row :gutter="[16, 16]" type="flex" align="middle">
            <a-col :xs="24" :sm="24" :md="8" :lg="7">
              <div class="field-label">Campaign</div>
              <a-select
                v-model="selectedCampaign"
                show-search
                allow-clear
                placeholder="Select a campaign"
                class="w-100"
                :loading="loadingDistinct"
                option-filter-prop="children"
                :filter-option="filterCampaignOption"
              >
                <a-select-option
                  v-for="opt in campaignOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </a-select-option>
              </a-select>
            </a-col>
            <a-col :xs="24" :sm="24" :md="10" :lg="10">
              <div class="field-label">Date range</div>
              <a-range-picker
                v-model="dateRange"
                class="w-100"
                format="YYYY-MM-DD"
                :placeholder="['Start date', 'End date']"
              />
            </a-col>
            <a-col :xs="24" :sm="24" :md="6" :lg="7" class="submit-col">
              <div class="field-label d-none d-md-block">&nbsp;</div>
              <a-button
                type="primary"
                size="large"
                block
                :loading="loadingData"
                :disabled="!canSubmit"
                @click="fetchCampaignData"
              >
                Submit
              </a-button>
            </a-col>
          </a-row>
          <p v-if="distinctError" class="hint-error mt-2 mb-0">{{ distinctError }}</p>
        </a-card>
      </a-col>

      <a-col :span="24">
        <a-card :bordered="false" class="results-card" :body-style="{ padding: resultBodyPadding }">
          <span slot="title">
            <span class="results-title">Results</span>
            <a-spin v-if="loadingData" class="ml-2" />
          </span>
          <template v-if="hasSubmitted && !loadingData">
            <a-alert
              v-if="submitError"
              type="error"
              show-icon
              :message="submitError"
              class="mb-3"
            />
            <div v-else-if="isEmptyResult" class="empty-state">
              <a-empty description="No Data Found" />
            </div>
            <div v-else-if="resultKind === 'table'" class="table-wrap">
              <a-table
                :columns="tableColumns"
                :data-source="tableRows"
                :pagination="tablePagination"
                :scroll="{ x: 'max-content' }"
                row-key="key"
                size="middle"
                bordered
              />
            </div>
            <div v-else-if="resultKind === 'list'" class="primitive-list">
              <div
                v-for="(item, index) in primitiveList"
                :key="'p-' + index"
                class="primitive-row"
              >
                <span class="item-index">{{ index + 1 }}.</span>
                <span>{{ formatCell(item) }}</span>
              </div>
            </div>
            <div v-else-if="resultKind === 'object'" class="object-result">
              <a-descriptions :column="1" bordered size="middle">
                <a-descriptions-item
                  v-for="(val, key) in singleObject"
                  :key="key"
                  :label="humanizeKey(key)"
                >
                  {{ formatCell(val) }}
                </a-descriptions-item>
              </a-descriptions>
            </div>
          </template>
          <p v-else-if="!hasSubmitted && !loadingData" class="text-muted mb-0 hint-idle">
            Choose a campaign and date range, then press Submit to load data.
          </p>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import moment from 'moment';

const ADMIN_URL = process.env.VUE_APP_ADMIN_URL;

function extractArrayFromResponse(data) {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'object') {
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.results)) return data.results;
    if (data.data && Array.isArray(data.data.data)) return data.data.data;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.campaigns)) return data.campaigns;
    if (data.success === true && Array.isArray(data.data)) return data.data;
  }
  return [];
}

function toCampaignOptions(rawList) {
  return rawList.map((item, index) => {
    if (item == null) {
      return { value: `__empty_${index}`, label: '(empty)' };
    }
    if (typeof item === 'string' || typeof item === 'number') {
      const s = String(item);
      return { value: s, label: s };
    }
    if (typeof item === 'object') {
      const value =
        item.campaign ??
        item.slug ??
        item.name ??
        item.value ??
        item.id ??
        item.code;
      const label =
        item.label ??
        item.name ??
        item.title ??
        item.campaign ??
        String(value ?? index);
      const v = value != null ? String(value) : JSON.stringify(item);
      return { value: v, label: String(label) };
    }
    return { value: String(item), label: String(item) };
  });
}

export default {
  name: 'CampaignData',
  data() {
    return {
      loadingDistinct: false,
      loadingData: false,
      distinctError: '',
      campaignOptions: [],
      selectedCampaign: undefined,
      dateRange: [],
      startDate: '',
      endDate: '',
      hasSubmitted: false,
      resultRows: [],
      resultKind: 'empty',
      singleObject: {},
      primitiveList: [],
      submitError: '',
    };
  },
  computed: {
    canSubmit() {
      return (
        this.selectedCampaign != null &&
        this.selectedCampaign !== '' &&
        this.startDate &&
        this.endDate
      );
    },
    isEmptyResult() {
      if (this.resultKind === 'table') {
        return !this.resultRows.length;
      }
      if (this.resultKind === 'list') {
        return !this.primitiveList.length;
      }
      if (this.resultKind === 'object') {
        return !Object.keys(this.singleObject).length;
      }
      return true;
    },
    tableColumns() {
      if (!this.resultRows.length) {
        return [];
      }
      const keySet = new Set();
      this.resultRows.forEach((row) => {
        if (row && typeof row === 'object') {
          Object.keys(row).forEach((k) => {
            if (k !== 'key') keySet.add(k);
          });
        }
      });
      const keys = Array.from(keySet);
      return keys.map((k) => ({
        title: this.humanizeKey(k),
        dataIndex: k,
        key: k,
        ellipsis: true,
      }));
    },
    tableRows() {
      return this.resultRows.map((row, i) => ({
        ...row,
        key: row.key != null ? row.key : `row-${i}`,
      }));
    },
    tablePagination() {
      const len = this.tableRows.length;
      if (len <= 10) return false;
      return { pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} records` };
    },
    resultBodyPadding() {
      return this.hasSubmitted && !this.loadingData && this.isEmptyResult ? '48px 24px' : '12px 24px';
    },
  },
  watch: {
    dateRange(val) {
      this.startDate = '';
      this.endDate = '';
      if (val && val.length === 2 && val[0] && val[1]) {
        this.startDate = moment(val[0]).format('YYYY-MM-DD');
        this.endDate = moment(val[1]).format('YYYY-MM-DD');
      }
    },
  },
  created() {
    this.loadDistinctCampaigns();
  },
  methods: {
    humanizeKey(key) {
      return String(key)
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatCell(val) {
      if (val == null) return '—';
      if (typeof val === 'object') {
        try {
          return JSON.stringify(val);
        } catch (e) {
          return String(val);
        }
      }
      return String(val);
    },
    filterCampaignOption(input, option) {
      const text = (option.componentOptions.children[0].text || '').toLowerCase();
      return text.includes((input || '').toLowerCase());
    },
    loadDistinctCampaigns() {
      this.loadingDistinct = true;
      this.distinctError = '';
      this.$axios
        .get(`${ADMIN_URL}/distinct-campaign`)
        .then((res) => {
          const list = extractArrayFromResponse(res.data);
          this.campaignOptions = toCampaignOptions(list);
        })
        .catch((err) => {
          this.distinctError =
            (err.response && err.response.data && err.response.data.message) ||
            'Could not load campaigns. Please try again.';
          this.campaignOptions = [];
        })
        .finally(() => {
          this.loadingDistinct = false;
        });
    },
    normalizeCampaignDataPayload(data) {
      const arr = extractArrayFromResponse(data);
      if (arr.length) {
        if (typeof arr[0] === 'object' && arr[0] !== null && !Array.isArray(arr[0])) {
          return { kind: 'table', rows: arr };
        }
        return { kind: 'list', rows: arr };
      }
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const inner = data.data;
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
          return { kind: 'object', obj: inner };
        }
        const skip = new Set(['success', 'message', 'meta']);
        const keys = Object.keys(data).filter((k) => !skip.has(k));
        const objectLike = keys.some((k) => typeof data[k] === 'object');
        if (!objectLike && keys.length) {
          return { kind: 'object', obj: data };
        }
      }
      return { kind: 'empty', rows: [] };
    },
    fetchCampaignData() {
      if (!this.canSubmit) return;
      this.loadingData = true;
      this.submitError = '';
      this.hasSubmitted = true;
      const params = {
        campaign: this.selectedCampaign,
        startDate: this.startDate,
        endDate: this.endDate,
      };
      this.$axios
        .get(`${ADMIN_URL}/campaign-data`, { params })
        .then((res) => {
          const normalized = this.normalizeCampaignDataPayload(res.data);
          if (normalized.kind === 'table') {
            this.resultKind = 'table';
            this.resultRows = normalized.rows;
            this.primitiveList = [];
            this.singleObject = {};
          } else if (normalized.kind === 'list') {
            this.resultKind = 'list';
            this.primitiveList = normalized.rows;
            this.resultRows = [];
            this.singleObject = {};
          } else if (normalized.kind === 'object') {
            this.resultKind = 'object';
            this.singleObject = normalized.obj || {};
            this.resultRows = [];
            this.primitiveList = [];
          } else {
            this.resultKind = 'table';
            this.resultRows = [];
            this.primitiveList = [];
            this.singleObject = {};
          }
        })
        .catch((err) => {
          this.resultKind = 'table';
          this.resultRows = [];
          this.primitiveList = [];
          this.singleObject = {};
          this.submitError =
            (err.response && err.response.data && err.response.data.message) ||
            (err.message && String(err.message)) ||
            'Request failed.';
          this.$message.error(this.submitError);
        })
        .finally(() => {
          this.loadingData = false;
        });
    },
  },
};
</script>

<style scoped>
.campaign-data-page {
  max-width: 1200px;
  margin: 0 auto;
}
.field-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.filter-card {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  border-radius: 8px;
}
.results-card {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  min-height: 200px;
}
.results-title {
  font-weight: 600;
}
.ml-2 {
  margin-left: 8px;
}
.mt-2 {
  margin-top: 8px;
}
.mb-0 {
  margin-bottom: 0;
}
.mb-3 {
  margin-bottom: 16px;
}
.text-muted {
  color: rgba(0, 0, 0, 0.45);
}
.w-100 {
  width: 100%;
}
.submit-col {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
@media (min-width: 768px) {
  .submit-col .ant-btn {
    max-width: 200px;
  }
}
.hint-error {
  color: #cf1322;
  font-size: 13px;
}
.hint-idle {
  padding: 24px 0;
  text-align: center;
}
.empty-state {
  padding: 32px 0;
}
.table-wrap {
  overflow-x: auto;
}
.primitive-list {
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
}
.primitive-row {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}
.primitive-row:last-child {
  border-bottom: none;
}
.primitive-list .item-index {
  color: rgba(0, 0, 0, 0.45);
  margin-right: 8px;
  min-width: 2em;
  display: inline-block;
}
.object-result {
  max-width: 720px;
}
.d-none {
  display: none;
}
@media (min-width: 768px) {
  .d-md-block {
    display: block;
  }
}
</style>
